import { Box, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { Fragment, useState } from "react";
import { getTranslation } from "../../utils/translate";
import useSWR from "swr";
import { fetcher } from "../../common/utils/fetcher";
import KeyValueCard from "../KeyValueCard";
import BarChart from "../shared/BarChart";
import { chooseInputComponent } from "../../common/utils/components";
import TableDisplay from "../TableDisplay";
import DataVisualiser from "../DataVisualiser";
import { InsightsChartSkeleton } from "./InsightsAsyncState";
import { insightsSectionClassName } from "../../common/utils/insightsDesignEnabled";

const BeefInsights = () => {
    const retailer = useSelector((state) => state.retailer);
    const [contestId, setContestId] = useState('cd992816-30d5-40c9-8092-0755c8eff0cd');
    const [beefCutId, setBeefCutId] = useState('ab9f2eee-f9e5-4c65-9564-bcd0fcd0e9ce');

    const { data: thisYearBeefPageViewsData, isLoading: thisYearBeefPageViewsLoading } = useSWR({
        url: 'page_views',
        fields: '*,url',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    retailer: {
                        _eq: retailer.id,
                    }
                },
                {
                    _or: [
                        {
                            url: {
                                _contains: '/recipe-list/',
                            }
                        },
                        {
                            url: {
                                _contains: '/recipe-list?beef_cut=',
                            }
                        },
                    ]
                }
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['url'],
        limit: -1,
        sort: ['-count'],
    }, fetcher);

    let thisYearBeefPageViews = null;

    if (thisYearBeefPageViewsData) {
        thisYearBeefPageViews = thisYearBeefPageViewsData;

        thisYearBeefPageViews = thisYearBeefPageViews.map((item) => {
            let id;
            
            // First, try to extract beef_cut parameter from query string
            const url = new URL(item.url, window.location.origin);
            const beefCutParam = url.searchParams.get('beef_cut');
            
            if (beefCutParam) {
                // Use the beef_cut parameter as the ID
                id = beefCutParam;
            } else {
                // If no beef_cut param, remove all query params and extract ID from path
                const pathWithoutQuery = item.url.split('?')[0];
                id = pathWithoutQuery
                    .replace('/recipe-list/', '')
                    .split('/')[0];
            }

            return {
                ...item,
                id: id,
            };
        });
    }

    // Extract unique IDs for fetching beef cut titles
    const beefCutIds = thisYearBeefPageViews 
        ? [...new Set(thisYearBeefPageViews.map(item => item.id).filter(id => id && id !== 'undefined' && id !== 'null'))]
        : [];

    // Fetch beef cut titles for the extracted IDs
    const { data: beefCutsData, isLoading: beefCutsDataLoading } = useSWR(
        beefCutIds.length > 0 ? {
            url: 'beef_product',
            fields: 'id,translations.*',
            filter: {
                id: {
                    _in: beefCutIds
                }
            },
            limit: -1
        } : null,
        fetcher
    );

    // Create a mapping of ID to title
    const beefCutTitlesMap = {};
    if (beefCutsData) {
        beefCutsData.forEach(beefCut => {
            const translatedBeefCut = getTranslation(beefCut, { key: 'languages_id', code: 'en-US' });
            beefCutTitlesMap[beefCut.id] = translatedBeefCut?.name || 'Unknown Beef Cut';
        });
    }

    // Update the data with titles and combine entries with the same ID
    if (thisYearBeefPageViews && beefCutsData && Object.keys(beefCutTitlesMap).length > 0) {
        // First, add titles to all items
        const itemsWithTitles = thisYearBeefPageViews.map((item) => ({
            ...item,
            title: beefCutTitlesMap[item.id] || `Beef Cut ${item.id}`,
        }));

        // Group by ID and sum the counts
        const groupedData = itemsWithTitles.reduce((acc, item) => {
            const existingItem = acc.find(groupedItem => groupedItem.id === item.id);
            
            if (existingItem) {
                // Sum the counts for the same ID
                existingItem.count += item.count;
            } else {
                // Add new item
                acc.push({ ...item });
            }
            
            return acc;
        }, []);

        thisYearBeefPageViews = groupedData;
    }

    console.log('thisYearBeefPageViews: ', thisYearBeefPageViews);
    console.log('beefCutTitlesMap: ', beefCutTitlesMap);
    console.log('beefCutIds: ', beefCutIds);

    const { data: thisYearPrimalBeefPageViewsData } = useSWR({
        url: 'page_views',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    url: {
                        _contains: '/primal/',
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['url'],
        limit: -1,
        sort: ['-count'],
    }, fetcher);

    let thisYearPrimalBeefPageViews = null;

    if (thisYearPrimalBeefPageViewsData) {
        thisYearPrimalBeefPageViews = thisYearPrimalBeefPageViewsData;
    }
    console.log('thisYearPrimalBeefPageViews: ', thisYearPrimalBeefPageViews);

    const formSections = [  
        {
            title: '',
            fields: [
                {
                    label: 'Contest',
                    name: 'contest_id',
                    type: 'm2o',
                    value: contestId,
                    handleInputChange: (value) => {
                        setContestId(value);
                    },
                    config: {
                        size: '12',
                        translations: [],
                        title: 'Contests',
                        url: '/contests',
                        searchOnly: 'CONTESTS',
                        detailsPayload: {
                            fields: '*,translations.*',
                        },
                        detailsUrl: `/contests`,
                        detailsResponseHandler: (data) => {
                            const translatedItem = getTranslation(data, { key: 'languages_code', code: 'en-US' })

                            return {
                                id: data?.id,
                                title: translatedItem?.tagline || "",
                                description: translatedItem?.heading || "",
                                imageUrl: `${process.env.REACT_APP_API_URL}/assets/${translatedItem?.featured_image}?key=brand-portal-image-card-square`,
                                url: `/contests/${data.id}`,
                                data,
                            };
                        },
                        payload: {
                            filter: {
                                '_or': [
                                    {
                                        retailers: {
                                            retailer_id: {
                                                '_eq': retailer.id
                                            }
                                        }
                                    },
                                ]
                            },
                            fields: '*, translations.*',
                            limit: 25,
                            meta: '*',
                        },
                        rows: [],
                        responseHandler: (data) => {
                            return data?.data?.map((item) => {
                                const translatedItem = getTranslation(item, { key: 'languages_code', code: 'en-US' })
                                return {
                                    id: item?.id,
                                    title: translatedItem?.tagline || "",
                                    description: translatedItem?.heading || "",
                                    imageUrl: `${process.env.REACT_APP_API_URL}/assets/${translatedItem?.featured_image}`,
                                    url: `/contests/${item.id}`,
                                    data: item,
                                };
                            }) || [];
                        },
                        headCells: [],
                        items: [],
                    },
                }, 
            ],
        },
    ];

    return (
        <Box className={insightsSectionClassName("insights-section--content")}>
            {
                (thisYearBeefPageViewsLoading || (beefCutIds.length > 0 && beefCutsDataLoading)) ? (
                    <InsightsChartSkeleton />
                ) : (
                    <>
                        <Box>
                            <DataVisualiser
                                data={thisYearBeefPageViews}
                                xKey="title"
                                yKey="count"
                                xLabel="Beef Cut"
                                yLabel="Page Views"
                                showXAxisValues={false}
                                title="Top Beef Cuts"
                                description="The most viewed beef cuts this year"
                                emptyMessage="No beef cut page views this year."
                            />
                        </Box>

                        {/* <Box>
                            <DataVisualiser
                                data={thisYearPrimalBeefPageViews}
                                xKey="url"
                                yKey="count"
                                xLabel="Primal"
                                yLabel="Count"
                                showXAxisValues={false}
                                title="Primal Beef Cut Page Views"
                                description="Primal Beef Cut Page Views"
                                horizontal={false}
                            />
                        </Box> */}
                    </>
                )
            }
        </Box >
    )
}

export default BeefInsights